import { Link } from "react-router-dom";
import { ArrowRight, Heart, Home, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const VALUES = [
  { icon: Home, title: "Dignity First", text: "Families choose their own items and purchase them affordably — a hand up, never a handout." },
  { icon: Users, title: "Community Powered", text: "Every item comes from a neighbor. Every dollar goes back into helping the next family." },
  { icon: Heart, title: "Hope Centered", text: "We focus on new beginnings — warm rooms, full kitchens, and beds for every child." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero overline="About Us" title="A FRESH START SHOULD" italic="feel like home." testid="about-headline">
        Fresh Start Furnishings is a pilot program rooted in Glendale, Arizona — built on a simple belief about what
        every family deserves.
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <FadeUp>
            <img
              src="https://images.unsplash.com/photo-1758598737810-7489fde716af?w=1200&q=80&auto=format&fit=crop"
              alt="A smiling family together at home"
              className="aspect-[4/5] w-full rounded-3xl border border-linen object-cover shadow-xl shadow-olive/10"
            />
          </FadeUp>
          <div>
            <FadeUp><Overline>Our Mission</Overline></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-3 font-serif text-3xl tracking-tight text-olive sm:text-4xl">
                No woman or family should have to start over in an <span className="italic text-burgundy">empty house.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2} className="mt-5 space-y-4 text-base leading-relaxed text-moss">
              <p>
                Fresh Start Furnishings was created to bridge the gap between people who have usable furniture they no
                longer need and families who are rebuilding their lives and need help turning a house into a home.
              </p>
              <p>
                We serve women and families coming out of domestic violence, homelessness, addiction recovery, poverty,
                transitional housing, and other major life transitions — offering donated furniture and household
                essentials at deeply affordable prices, never as a free giveaway.
              </p>
              <p>
                We are currently a <strong className="text-ink">pilot program</strong>, growing carefully across
                Glendale and the West Valley. Every donation, every purchase, and every dollar helps us prove that this
                model works — so we can help more families, sooner.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="mt-24 grid gap-6 md:grid-cols-3">
          {VALUES.map((v, i) => (
            <FadeUp key={v.title} delay={i * 0.12}>
              <div data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`} className="bento-card h-full">
                <v.icon className="h-8 w-8 text-burgundy" />
                <h3 className="mt-4 font-serif text-2xl text-olive">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-moss">{v.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.2} className="mt-20 grid items-center gap-10 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <Overline>Why It Matters</Overline>
            <h2 className="mt-3 font-serif text-3xl tracking-tight text-olive sm:text-4xl">
              A bed to sleep in. A table to gather around. <span className="italic text-burgundy">A place that feels like theirs.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-moss">
              When a family walks into a furnished home instead of empty rooms, everything changes — children do
              homework at a real table, dinners happen together, and a new beginning actually feels like one.
            </p>
            <Link to="/support" data-testid="about-support-button" className="btn-accent mt-7">
              Help Fund a Fresh Start <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <img
            src="https://images.unsplash.com/photo-1546791844-1a7fc3d7c809?w=1200&q=80&auto=format&fit=crop"
            alt="A child reading comfortably at home"
            className="order-1 aspect-[4/3] w-full rounded-3xl border border-linen object-cover shadow-xl shadow-olive/10 lg:order-2"
          />
        </FadeUp>
      </section>

      <Marquee />
    </div>
  );
}
